import React, { useEffect, useState } from "react";
import cx from "classnames";
import { generatePath } from "react-router-dom";
import { browserHistory, Link } from "react-router"; // v3 style for navigate() in v5/v6rc1
import moment from "moment";
import useSWR, { mutate } from "swr";
import { addGlobalMessage } from "../../actions/globalmessages";
import { useDispatch } from "react-redux";
import Select from "react-select-new";

import { fetcher } from "../../components/Overview/Overview";
import { ELEARNING_WEEKS_DEFAULT, eventTypeToNL, NEGATIVE_EVENT_WEEKS_OFFSET } from "../../constants";

import { CompetenceProfileSelector } from "../competenceprofiles/CompetenceProfileSelector";
import { CompetenceProfileCard } from "./CompetenceProfileCard";
import { dateOrNow, Event, filterEventTypeName } from "./Event";
import Dialog from "rc-dialog";
import { remoteSetCompetenceProfile, remoteSaveCourseDetails } from "../../queries/courses";
import { remoteSaveEventDetails, remoteDeleteEvent, remoteBulkSaveEventDetails } from "../../queries/events";
import { CompetenceProfileSelectorListFilter } from "../competenceprofiles/CompetenceProfileSelectorListFilter";
import ProgressBar from "../../components/ProgressBar";

const formatOptions = { day: "2-digit", month: "2-digit", year: "numeric" };
const formatOptionsTime = { hour: "2-digit", minute: "2-digit" };

export default function CoursesEdit(props) {
    //temp toggle for switching
    const [overviewFilter, setOverviewFilter] = useState(true);

    const dispatch = useDispatch();
    const [pleaseWait, setPleaseWait] = useState(false);
    const [errors, setErrors] = useState([]);
    const [courseErrors, setCourseErrors] = useState([]);
    const [eventErrors, setEventErrors] = useState([]);

    const [confirmCustomEvents, setConfirmCustomEvents] = useState(false);
    const [confirmTemplateEvents, setConfirmTemplateEvents] = useState(false);

    const [course, setCourse] = useState(props?.params?.itemID);
    const [courseReference, setCourseReference] = useState("");
    const [employerSpecific, setEmployerSpecific] = useState(false);
    const [trainer, setTrainer] = useState(false);
    const [trainerContact, setTrainerContact] = useState(false);
    const [expectedStudents, setExpectedStudents] = useState("");

    const [competenceProfileUUID, setCompetenceProfileUUID] = useState(false); // The selected competence profile
    const [competenceProfile, setCompetenceProfile] = useState(false); // The selected competence profile
    const [defaultMinDate, setDefaultMinDate] = useState(moment().subtract(parseInt(NEGATIVE_EVENT_WEEKS_OFFSET), "weeks").toDate()); // Set the default to use in events

    const [extraEvent, setExtraEvent] = useState(false); // The event to add in custom event template mode

    const [customEvents, setCustomEvents] = useState(false);
    const [missingEvents, setMissingEvents] = useState([]);

    // Events!
    const [splittableEvents, setSplittableEvents] = useState([]); // Events which can be split
    const [scheduledEvents, setScheduledEvents] = useState([]); // Event which are currently scheduled in the course.

    const [eventAllowedTrainers, setEventAllowedTrainers] = useState({});

    const [eventTemplates, setEventTemplates] = useState([]);
    const [courseEvents, setCourseEvents] = useState(!props?.params?.itemID ? [] : false);

    const [showWarningModal, setShowWarningModal] = useState(false);

    const [saveProgress, setSaveProgress] = useState([0, 0]);

    const { data: trainers } = useSWR(() => "/v1/trainers/?fields=uuid,name,is_trainer&pagination=false&is_active=1", fetcher);
    const { data: trainerContactPersons } = useSWR(
        () => (trainer ? `/v1/trainers/${trainer?.uuid}/users/?pagination=false&is_trainer_contact=true&fields=uuid,first_name,last_name,last_name_prefix` : null),
        fetcher
    );

    const { data: remoteEventTemplates, mutate: mutateRemoteEventTemplates } = useSWR(
        () =>
            competenceProfileUUID
                ? `/v1/competenceprofiles/${competenceProfileUUID}/eventtemplates/?is_active=1&fields=uuid,name,parent,roles,type,prerequisites,location,is_active,is_editable`
                : null,
        fetcher
    );
    const { data: locations } = useSWR(
        () => (trainer ? `/v1/locations/?company=${trainer?.uuid}&is_active=1&pagination=false&fields=uuid,name,street_name,number,postal_code,city` : null),
        fetcher
    );

    // Edit mode when a course id is present
    const { data: courseDetails, mutate: mutateCourseDetails } = useSWR(() => (course ? `/v1/courses/${course}/` : null), fetcher);
    const { data: courseEventsDetails, mutate: mutateCourseEventDetails } = useSWR(
        () =>
            course && locations && eventTemplates.length
                ? `/v1/courses/${course}/events/?fields=uuid,name,type,course,template,start_datetime,end_datetime,location,competence_profile,event_roles,is_editable&pagination=false`
                : null,
        fetcher
    );
    //const {data: courseEventsDetails} = useSWR(() => course && locations && eventTemplates.length > 0 ? `/v1/courses/${course}/events/?pagination=false` : null, fetcher);
    const { data: trainerContactUser } = useSWR(
        () => (course && courseDetails ? `/v1/users/${courseDetails.contact_user}/?fields=uuid,first_name,last_name_prefix,last_name` : null),
        fetcher
    );
    const { data: competenceProfileDetails } = useSWR(() => (competenceProfileUUID ? `/v1/competenceprofiles/${competenceProfileUUID}/` : null), fetcher);

    useEffect(() => {
        if (courseDetails?.is_editable === false && course) {
            browserHistory.push(
                generatePath("/courses/edit/:uuid/details", {
                    uuid: course
                })
            );
        }
    }, [courseDetails, course]);

    useEffect(() => {
        if (customEvents) {
            const missingTemplates = [];

            eventTemplates.forEach((eventTemplate) => {
                if (!scheduledEvents.some((e) => e.template === eventTemplate.template)) {
                    // not top-leve scheduled
                    if (splittableEvents?.some((sE) => sE.uuid === eventTemplate.template)) {
                        // item can contain child elements
                        if (
                            !eventTemplates
                                .filter((e) => e.template_parent === eventTemplate.template)
                                .some((child) => scheduledEvents.some((sE) => sE.template === child.template))
                        ) {
                            missingTemplates.push(eventTemplate);
                        }
                    } else {
                        if (eventTemplate?.template_parent) {
                            // item can have a parent
                            if (!scheduledEvents.some((sE) => sE.template === eventTemplate.template_parent)) {
                                missingTemplates.push(eventTemplate);
                            }
                        } else {
                            missingTemplates.push(eventTemplate);
                        }
                    }
                }
            });
            setMissingEvents(missingTemplates);
        }
    }, [scheduledEvents]);

    // If there is only 1 trainer, set it
    useEffect(() => {
        if (Array.isArray(trainers) && trainers?.length === 1) {
            setTrainer(trainers[0]);
        }
    }, [trainers]);

    // Remove the selected contact when the trainer is changed
    useEffect(() => {
        if (!trainerContact) {
            setTrainerContact(false);
        }
    }, [trainer]);

    // When the trainer contact persons list changes, update the selected contact person
    useEffect(() => {
        if (Array.isArray(trainerContactPersons)) {
            if (trainerContactPersons.length === 1) {
                // If there is only 1 trainer, set it
                setTrainerContact(trainerContactPersons[0]);
            } else if (!courseDetails) {
                // If there is no current course (we are creating) try to find the
                // currently logged-in user in the list and set it.
                const newContact = trainerContactPersons?.find((trainerContact) => trainerContact.uuid === props?.authentication?.user?.uuid) || false;
                setTrainerContact(newContact);
            }
        }
    }, [trainerContactPersons]);

    useEffect(() => {
        setCompetenceProfile(competenceProfileDetails);

        if (competenceProfileDetails?.min_date) {
            if (moment(defaultMinDate).isSameOrBefore(competenceProfileDetails.min_date)) {
                // the CP restriction trumps the default if the default is further in the past
                setDefaultMinDate(moment(competenceProfileDetails.min_date).toDate());
            }
        } else {
            setDefaultMinDate(moment().subtract(parseInt(NEGATIVE_EVENT_WEEKS_OFFSET), "weeks").toDate()); // reset
        }
    }, [competenceProfileDetails]);

    useEffect(() => {
        setTrainerContact(trainerContactUser);
    }, [trainerContactUser]);

    useEffect(() => {
        if (courseDetails) {
            setCompetenceProfileUUID(courseDetails.competence_profile);
            setCourse(courseDetails.uuid);
            setCourseReference(courseDetails.your_reference);
            if (trainer === false || trainer?.uuid !== courseDetails?.trainer_details?.uuid) {
                setTrainer({
                    uuid: courseDetails.trainer_details.uuid,
                    name: courseDetails.trainer_details.name
                });
            }
            setTrainerContact(courseDetails.contact_user ? trainerContactPersons?.find((trainerContact) => trainerContact.uuid === courseDetails.contact_user) : false);
            setEmployerSpecific(courseDetails.employer_specific);
            setExpectedStudents(courseDetails.expected_students);
            mutateRemoteEventTemplates();
            mutateCourseEventDetails();
        }
    }, [courseDetails, trainerContactPersons]);

    // Parse eventTemplates
    useEffect(() => {
        if (remoteEventTemplates?.count >= 0) {
            // Initially all eventTemplates should be scheduled when creating a course, this might change later on when
            // default events can be deleted and custom events can be added.
            let processed = [];

            remoteEventTemplates.results?.forEach((template, idx) => {
                processed.push(
                    Object.assign(
                        {},
                        {
                            uuid: null,
                            name: template.name,
                            type: template.type,
                            template: template.uuid,
                            course: null,
                            template_parent: template.parent,
                            trainer: trainer,
                            event_roles: [
                                {
                                    template: template.roles[0].uuid,
                                    instructors: []
                                }
                            ],
                            primary_instructor: null,
                            location: null,
                            dateStart: null,
                            timeTo: null, // Used to compare if values are changed
                            timeFrom: null, // Used to compare if values are changed
                            start_datetime: null,
                            end_datetime: null,
                            prerequisites: template.prerequisites,
                            competence_profile: null
                        }
                    )
                );
            });

            // We create a look-up table with which we can determine if a UUID can be split
            const splitableEvents =
                // Cast the list through set to become unique
                [
                    ...new Set(
                        // walk all templates and only get those which have a parent, and store the parent in the array
                        processed.filter((template) => template.template_parent !== null).map((template) => template.template_parent)
                    )
                ];
            setSplittableEvents(
                splitableEvents.map((eventUUID) => {
                    return {
                        uuid: eventUUID,
                        children: processed.filter((e) => e.template_parent === eventUUID)
                    };
                })
            );

            // store processed
            setEventTemplates(processed);
        }
    }, [remoteEventTemplates]);

    // Parse course events
    useEffect(() => {
        // we check if courseEventsDetails was set (SWR completed) but not check if its count is > 0
        // This is done because we can receive an empty result
        if (courseEventsDetails?.length >= 0) {
            let processed = [];

            courseEventsDetails?.forEach((eventDetail, idx) => {
                processed.push(
                    Object.assign(
                        {},
                        {
                            uuid: eventDetail.uuid,
                            name: eventDetail.name,
                            type: eventDetail.type,
                            course: eventDetail.course,
                            template: eventDetail.template,
                            template_parent: null,
                            trainer: trainer, // FIXME Might have to move this logic, trainer could be switched later on. Maybe add training for all events when attempting to save course/events
                            event_roles: [
                                {
                                    template: eventDetail.event_roles[0].template,
                                    instructors: eventDetail.event_roles[0].instructors
                                }
                            ],
                            primary_instructor: eventDetail.event_roles[0].instructors.find((instructor) => instructor.primary === true),
                            location: locations?.find((location) => location.uuid === eventDetail.location),
                            start_datetime: eventDetail.start_datetime,
                            end_datetime: eventDetail.end_datetime,
                            prerequisites: eventTemplates.find((e) => e.template === eventDetail.template)?.prerequisites || [],
                            dateStart: eventDetail.start_datetime !== null ? moment(eventDetail.start_datetime).toDate() : null,
                            competence_profile: eventDetail.competence_profile, // FIXME We dont need this information here, only in the save
                            is_editable: eventDetail.is_editable
                        }
                    )
                );
            });

            // stored processed as events
            setCourseEvents(processed);
        }
    }, [courseEventsDetails]);

    // Parse courseEvents and eventTemplates to get our correct work-set
    useEffect(() => {
        if (scheduledEvents.length === 0 && eventTemplates.length > 0 && (courseEvents || (!courseDetails && !course))) {
            let splittedItems = [];
            let scheduledSplittableParent = [];
            let removedChildren = [];

            // handle events which are dictated by previous saving
            courseEvents?.forEach((courseEvent) => {
                // check if any splitable parents are scheduled
                if (splittableEvents.find((splittableEvent) => splittableEvent.uuid === courseEvent.template)) {
                    scheduledSplittableParent.push(courseEvent.template);
                }

                // check and exclude eventTemplate when a child from a splitted event has stored information
                splittableEvents.forEach((parent) => {
                    // only force hiding when the parent element has no scheduled information (if it does, only the parent is shown)
                    if (!scheduledSplittableParent.includes(parent.uuid)) {
                        parent.children.forEach((child) => {
                            if (child.template === courseEvent.template) {
                                splittedItems.push(parent.uuid);
                            }
                        });
                    }
                });
            });

            // find out if certain items are children of splittable events and if so, mark them as not visible
            splittableEvents.forEach((event) => {
                // find if any of the splittable events is either open or scheduled
                if (!splittedItems.includes(event.uuid) && !scheduledSplittableParent.includes(event.uuid)) {
                    // if not, we remove the child items
                    eventTemplates.filter((e) => e.template_parent === event.uuid).forEach((e) => removedChildren.push(e.template));
                }
            });

            // walk the eventTemplates and create a new list of events either based on the template or on an already scheduled item
            const scheduledEvents = eventTemplates.map((eventTemplate) => {
                let event = false;

                let courseEvent = courseEvents.find((e) => e.template === eventTemplate.template);

                // if event is in courseEvents, use courseEvents version
                if (courseEvent) {
                    event = courseEvent;
                } else {
                    event = eventTemplate;
                }

                return event;
            });

            setScheduledEvents(
                scheduledEvents
                    // filter scheduled event parents when any of their childs contain information (parent is splitted)
                    .filter((event) => !splittedItems.includes(event.template))
                    // filter scheduled events so child elements are not displayed when the parent has scheduled content
                    .filter((event) => !scheduledSplittableParent.includes(event.template_parent))
                    // remove children for splitable events which have neither a parent set nor a child set
                    .filter((event) => !removedChildren.includes(event.template))

                // NOTE: above filtering is intentionaly like this. When the situation occurs where both the parent
                // and any child element contains scheduled data, the child information is hidden
            );
        }
    }, [courseEvents, eventTemplates]);

    // if course (UUID) changes, set it on all scheduledEvents
    useEffect(() => {
        let newScheduledEvents = [...scheduledEvents];

        for (let index = 0; index < newScheduledEvents.length; index++) {
            newScheduledEvents[index].course = course;
        }

        setScheduledEvents(newScheduledEvents);
    }, [course]);

    const deleteEvent = (scheduledIndex) => {
        const newEvents = [...scheduledEvents];
        newEvents.splice(scheduledIndex, 1);
        setScheduledEvents(newEvents);
    };

    const addEvent = (eventTemplateUUID) => {
        const events = [];
        const newEvent = eventTemplates.find((e) => e.template === eventTemplateUUID);

        if (newEvent) {
            newEvent?.prerequisites?.forEach((prereq) => {
                if (!scheduledEvents.find((sE) => sE.uuid === prereq)) {
                    const newPrereqEvent = eventTemplates.find((e) => e.template === prereq);
                    if (newPrereqEvent) {
                        events.push(newPrereqEvent);
                    }
                }
            });
            events.push(newEvent);

            setScheduledEvents((curState) => [...curState, ...events]);
        }
    };

    // When updating an event, trigger this function to update the scheduledEvents
    const updateScheduledEvent = (edittedEvent) => {
        if (edittedEvent?.current_index >= 0) {
            let updatedScheduledEvents = [...scheduledEvents];

            updatedScheduledEvents[edittedEvent.current_index] = edittedEvent;

            // When changing the starting date all events below/after the current one should also get changed
            // Only if the following events have not been saved before (have no uuid)
            for (let index = edittedEvent.current_index; index < updatedScheduledEvents.length; index++) {
                if (updatedScheduledEvents[index].uuid === null) {
                    updatedScheduledEvents[index].date_changed = updatedScheduledEvents[index].dateStart !== edittedEvent.dateStart;
                    updatedScheduledEvents[index].time_from_changed = updatedScheduledEvents[index].timeFrom !== edittedEvent.timeFrom;
                    // updatedScheduledEvents[index].time_to_changed = updatedScheduledEvents[index].timeTo !== edittedEvent.timeTo;
                    updatedScheduledEvents[index].location_changed = updatedScheduledEvents[index].location !== edittedEvent.location;

                    updatedScheduledEvents[index].instructors_changed = false;
                    if (edittedEvent?.event_roles?.[0]?.instructors?.length >= 0) {
                        if (updatedScheduledEvents[index].event_roles[0].instructors?.length === edittedEvent?.event_roles?.[0]?.instructors?.length) {
                            // If all instr id's are equal it means no changes are done
                            updatedScheduledEvents[index].instructors_changed = !updatedScheduledEvents[index].event_roles[0].instructors.every((s_instr) =>
                                edittedEvent?.event_roles?.[0]?.instructors.find((instr) => instr.uuid === s_instr.uuid)
                            );
                        } else {
                            updatedScheduledEvents[index].instructors_changed = true;
                        }
                    }

                    updatedScheduledEvents[index].dateStart = edittedEvent.dateStart;
                    updatedScheduledEvents[index].timeFrom = edittedEvent.timeFrom;
                    // updatedScheduledEvents[index].timeTo = edittedEvent.timeTo;
                    updatedScheduledEvents[index].start_datetime = edittedEvent.start_datetime;
                    // updatedScheduledEvents[index].end_datetime = edittedEvent.end_datetime;
                    updatedScheduledEvents[index].location = edittedEvent.location;
                    updatedScheduledEvents[index].event_roles[0] = {
                        ...updatedScheduledEvents[index].event_roles[0],
                        instructors:
                            edittedEvent?.event_roles?.[0]?.instructors
                                // This filter makes sure that when an instructor is passed (by auto-propagation from another event) which was is allowed in this event, is discarded
                                ?.filter((instr) => eventAllowedTrainers?.[index]?.findIndex((allowedInstr) => allowedInstr.uuid === instr.uuid) >= 0) || []
                    };
                }
            }

            // If the from-date changes, update all of the prerequisites along with it
            if (edittedEvent.dateStart) {
                // use the start_datetime or construct one based off a date-only object
                const start_datetime = edittedEvent.start_datetime || new Date(edittedEvent.dateStart).toISOString();

                edittedEvent.prerequisites.forEach((prereq) => {
                    let prIndex = updatedScheduledEvents.findIndex((se) => se.template === prereq);

                    if (updatedScheduledEvents[prIndex].type === "elearning") {
                        // Subtract 4 weeks, but need to check if the elearning has custom weeks
                        updatedScheduledEvents[prIndex].start_datetime = moment(start_datetime).subtract(parseInt(ELEARNING_WEEKS_DEFAULT), "weeks");
                        updatedScheduledEvents[prIndex].end_datetime = moment(start_datetime);

                        // e-learnings should not have a location, explicitly set it to false
                        // updatedScheduledEvents[prIndex].location = false

                        // FIXME: Might not need this else the only prerequisits are elearnings
                    } else {
                        updatedScheduledEvents[prIndex].location = edittedEvent.location;
                        updatedScheduledEvents[prIndex].start_datetime = moment(start_datetime);
                        if (edittedEvent.end_datetime) {
                            updatedScheduledEvents[prIndex].end_datetime = moment(edittedEvent.end_datetime);
                        } else {
                            updatedScheduledEvents[prIndex].end_datetime = moment(start_datetime);
                        }
                    }
                });
            }

            // Add primary to the correct instructor
            updatedScheduledEvents[edittedEvent.current_index].event_roles[0].instructors = edittedEvent.event_roles[0]?.instructors?.map((instr) => {
                return {
                    uuid: instr.uuid,
                    primary: instr.uuid === edittedEvent?.primary_instructor?.uuid,
                    full_name: instr.full_name
                };
            });

            // If the event should be split the parent event has to be removed and replaced by it's child events.
            if (edittedEvent.split_event) {
                // mark this item as scheduled for deletion as we have split the item, the parent should not exist anymore
                // if (edittedEvent.uuid) {
                updatedScheduledEvents[edittedEvent.current_index].mark_delete = true;
                updatedScheduledEvents[edittedEvent.current_index].split_event = false;
                console.info("marked to delete", edittedEvent.current_index, updatedScheduledEvents[edittedEvent.current_index]);
                // }

                const prevSiblings = updatedScheduledEvents.slice(0, edittedEvent.current_index);
                const addedChildren = eventTemplates
                    .filter((event) => event.template_parent === edittedEvent.template)
                    .map((event) => {
                        return {
                            ...event,
                            dateStart: edittedEvent.dateStart,
                            start_datetime: edittedEvent.start_datetime,
                            end_datetime: edittedEvent.end_datetime,
                            location: edittedEvent.location,
                            event_roles: [
                                {
                                    ...event.event_roles[0],
                                    instructors: edittedEvent.event_roles[0].instructors
                                }
                            ]
                        };
                    });

                // now skip the split parent, and add the remaining siblings:
                const nextSiblings = updatedScheduledEvents.slice(edittedEvent.current_index);

                console.info("previous siblings", prevSiblings.map(e => e.name));
                console.info("added children", addedChildren.map(e => e.name));
                console.info("next siblings (including self)", nextSiblings.map(e => e.name));
                updatedScheduledEvents = [...prevSiblings, ...addedChildren, ...nextSiblings];
                console.info("post split", updatedScheduledEvents.map(e => e.name));
            }

            setScheduledEvents(updatedScheduledEvents);
        }
    };

    // Save the course details
    const saveCourse = async (postAction = false, hardRedirect) => {
        setPleaseWait(true);
        setShowWarningModal(false);

        let saveErrors = [];

        if (!trainer) {
            saveErrors.push({ field: "trainer", error: "Er is geen opleider gekozen" });
        }

        if (!competenceProfile) {
            saveErrors.push({ field: "competence_profile", error: "Er is geen competentieprofiel gekozen" });
        }

        if (!courseReference) {
            saveErrors.push({ field: "your_reference", error: "Er is geen cursusreferentie opgegeven" });
        }

        if (!trainerContact) {
            saveErrors.push({ field: "trainer_contact", error: "Er is geen contactpersoon opleider gekozen" });
        }

        if (!expectedStudents) {
            saveErrors.push({ field: "expected_students", error: "Het verwachte aantal cursisten is niet opgegeven" });
        }

        scheduledEvents.some(event => {
            if (!event.start_datetime) {
                saveErrors.push({
                    field: "event",
                    error: "Niet alle toetsmomenten hebben een geldige datum en tijd"
                });
                return true;
            }
            return false;
        });

        // always set the error state, also to reset when there are no errors.
        setErrors(saveErrors);

        if (saveErrors.length === 0) {
            let courseDetails = {
                competence_profile: competenceProfileUUID,
                trainer: trainer.uuid,
                expected_students: expectedStudents,
                contact_user: trainerContact?.uuid,
                your_reference: courseReference,
                employer_specific: employerSpecific
            };

            let courseUUID = "";
            let errors = [];

            setSaveProgress([1, scheduledEvents.length + 1]);
            let courseDetailResponse = await remoteSaveCourseDetails(course, courseDetails);

            if (courseDetailResponse.errors.length > 0) {
                let remoteErrors = [];

                if (courseDetailResponse?.errors?.findIndex((el) => el.field === "expected_students") >= 0) {
                    remoteErrors.push({ field: "expected_students", error: "Het verwachte aantal cursisten is niet opgegeven of minder dan één" });
                }
                if (courseDetailResponse?.errors?.findIndex((el) => el.field === "non_field_errors") >= 0) {
                    remoteErrors.push({ field: "non_field_errors", error: "Opleider is niet gekoppeld aan het geselecteerde competentie profiel." });
                }
                setCourseErrors(remoteErrors);

                dispatch(
                    addGlobalMessage({
                        title: "Cursus kon niet worden opgeslagen",
                        type: "error",
                        icon: "fa fa-exclamation-triangle"
                    })
                );
            } else {
                courseUUID = courseDetailResponse?.result?.uuid;

                setCourseErrors(undefined);

                if (course !== courseUUID) {
                    setCourse(courseUUID);

                    // const detailPath = generatePath("/courses/edit/:uuid", {
                    //     uuid: courseUUID
                    // });
                    //
                    // browserHistory.push(detailPath);
                }

                let savedEvents = await saveEvents(courseUUID);

                if (savedEvents === true) {
                    dispatch(
                        addGlobalMessage({
                            title: "Cursus is succesvol opgeslagen"
                        })
                    );

                    setScheduledEvents([]);
                    mutateCourseDetails();

                    if (postAction !== false) {
                        const url = generatePath(postAction, { uuid: courseUUID });
                        if (hardRedirect === true) {
                            window.location.href = url;
                        } else {
                            browserHistory.push(url);
                        }
                        // we hard break the save as to prevent the fallback browserhistory below
                        return true;
                    }
                }

                // When event errors occur, we do want to make sure the url is correct. Because saving the course was succesful
                browserHistory.push(
                    generatePath("/courses/edit/:uuid/", {
                        uuid: courseUUID
                    })
                );
            }
        }
        setPleaseWait(false);
    };

    // Wrapper function to simulate the bulk result error information
    // PLEASE NOTE: updates are sent individually, there is no possibility to rollback changes for all calls when an
    // individual request fails. This in contrast to the initial (bulk) save which can rollback everything when an
    // individual request fails.
    const saveEventsNonBulk = async (courseUUID, data) => {
        const errors = [];
        const result = [];

        // Await all promise results from the individual saves
        // await Promise.all(
        //     data.map(async (item, idx) => {
        //         const itemResult = await remoteSaveEventDetails(courseUUID, item.uuid, item);
        //         errors[idx] = itemResult?.errors || [];
        //         result[idx] = itemResult?.result;
        //     })
        // );
        // const wait = (n) => new Promise((resolve) => setTimeout(() => resolve(n), 1000));

        for (let i = 0; i < data.length; i++) {
            const progress = [i + 1, data.length + 1];
            setSaveProgress(progress);
            const item = data[i];
            if (item.mark_delete) {
                if (!item.uuid) {
                    // probably an event that was never saved before, no need to delete.
                    continue;
                }
                // console.info("deleting", item.uuid);
                const itemResult = await remoteDeleteEvent(courseUUID, item.uuid);
                // const itemResult = {};
                errors[i] = itemResult?.errors || [];
                result[i] = itemResult?.result;
            } else {
                // console.info(item.uuid ? `saving ${item.name}` : `creating ${item.name}`);
                const itemResult = await remoteSaveEventDetails(courseUUID, item.uuid, item);
                // const itemResult = {};
                errors[i] = itemResult?.errors || [];
                result[i] = itemResult?.result;
            }
        }
        // await data.map(async (item, idx) => {
        //     // const itemResult = await remoteSaveEventDetails(courseUUID, item.uuid, item);
        //     const progress = [idx + 1, data.length];
        //     console.info("saving", progress);
        //     setSaveProgress(progress);
        //     const itemResult = await wait({result: {}});
        //     errors[idx] = itemResult?.errors || [];
        //     result[idx] = itemResult?.result;
        // });
        setSaveProgress([0, 0]);

        return {
            result,
            errors
        };
    };

    const saveEvents = async (courseUUID) => {
        let calls = [];
        let errors = [];

        console.info("saving", scheduledEvents);

        const data = scheduledEvents.map((scheduledEvent) => {
            if (scheduledEvent.mark_delete) {
                // we only need the UUID to delete it:
                return {
                    uuid: scheduledEvent.uuid,
                    mark_delete: scheduledEvent.mark_delete
                };
            }

            const event = {
                ...scheduledEvent,
                course: courseUUID,
                competence_profile: competenceProfileUUID,
                trainer: trainer?.uuid,
                location: scheduledEvent.location?.uuid
            };
            // Mark each datetime as UTC while keeping the time and then convert to an ISO string:
            // When converting to JSON, each object would be converted using .toUTCString() which would result in a different time.
            ["start_datetime", "end_datetime"].forEach((key) => {
                const value = scheduledEvent[key];
                if (value && moment.isMoment(value)) {
                    event[key] = value.utc(true).toISOString();
                }
            });
            return event;
        });

        // detect if contains updates else bulk save
        const hasSavedItems = data.some((o) => o.uuid);

        // either do a bulk save (Create) or an update per item (Update)
        // console.info("SAVING!", {hasSavedItems, data});
        // return false;
        const eventDetailResponse = hasSavedItems ? await saveEventsNonBulk(courseUUID, data) : await remoteBulkSaveEventDetails(courseUUID, data);

        // console.info("returning false");
        // return false;

        errors = eventDetailResponse?.errors || [];

        if (errors.some((o) => o.length !== 0) === false) {
            return true;
        }

        setCourseErrors((currentState) => {
            return [
                ...(currentState || []),
                {
                    field: "events",
                    error: "Sommige toetsmomenten zijn niet correct ingevuld, of missen bepaalde informatie. Controleer de toetsmomenten op fouten."
                }
            ];
        });

        setEventErrors(errors);

        return false;
    };

    const checkDatesApart = (events) => {
        if (events.length < 2) return false;

        const sortedEvents = events
            .filter((e) => !!e.dateStart && e.type !== "elearning")
            .map((event) => ({
                ...event,
                parsedDate: new Date(event.dateStart)
            }))
            .sort((a, b) => a.parsedDate - b.parsedDate);

        for (let i = 0; i < sortedEvents.length - 1; i++) {
            for (let j = i + 1; j < sortedEvents.length; j++) {
                const date1 = sortedEvents[i].parsedDate;
                const date2 = sortedEvents[j].parsedDate;
                const differenceInDays = (date2 - date1) / (1000 * 3600 * 24);

                if (differenceInDays >= 93) {
                    return true;
                }
            }
        }

        return false;
    };

    const onSaveClick = (path) => {
        if (competenceProfile?.warn_if_events_are_too_far_apart && checkDatesApart(scheduledEvents)) {
            setShowWarningModal(path);
        } else {
            saveCourse(path, true);
        }
    };

    const [saving, savingTotal] = saveProgress;
    // const [saving, savingTotal] = [2, 3];

    return (
        <>
            <div className="section-top">
                {course && (
                    <>
                        <h1 className="form__title">Cursus bewerken</h1>
                        <div className="section-buttons">
                            <Link
                                to={generatePath("/courses/edit/:uuid/details", {
                                    uuid: course
                                })}
                                className="link"
                            >
                                Naar cursusdetails{" "}
                            </Link>
                        </div>
                    </>
                )}

                {!course && <h1 className="form__title">Cursus toevoegen</h1>}
            </div>

            {trainers?.length > 1 && (
                <div className="form-section">
                    <div className="form-section__row row">
                        <div className="col-12">
                            <h2 className="form-section__title">Selecteer een opleider</h2>

                            <div className="form-section__row row">
                                <div className="col-8">
                                    <label className="form-input__label">Opleider *</label>
                                    <Select
                                        name="trainer"
                                        value={trainer}
                                        className="react-select-new"
                                        classNamePrefix="react-select"
                                        onChange={setTrainer}
                                        controlShouldRenderValue={true}
                                        options={trainers}
                                        getOptionLabel={(trainer) => trainer.name}
                                        getOptionValue={(trainer) => trainer.uuid}
                                        isClearable={true}
                                        backspaceRemovesValue={true}
                                        placeholder={props?.trainers?.length > 0 ? "" : ""}
                                        noOptionsMessage={() => "Geen resultaten"}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {trainer && (
                <>
                    <div className="form-section">
                        {/*<a href={""} onClick={e => {*/}
                        {/*    e.preventDefault();*/}
                        {/*    setOverviewFilter(!overviewFilter);*/}
                        {/*}} style={{ position: 'absolute', right: 0}}>Stijl wisselen</a>*/}
                        {overviewFilter ? (
                            <CompetenceProfileSelectorListFilter
                                setReturnValue={async (cp) => {
                                    setScheduledEvents([]); // explicit clear because we normally only populate scheduled events on an empty set and specific situations. now we want to force this
                                    setEventAllowedTrainers([]); // explicit clear here as this is done so previously and we dont want to break stuff. There are some edge-cases when making a useEffect() on competenceProfielUUID
                                    await remoteSetCompetenceProfile(course, cp.uuid);
                                    setCompetenceProfileUUID(cp.uuid);
                                    mutateRemoteEventTemplates();
                                }}
                                competenceProfileDetails={competenceProfileDetails}
                                trainer={trainer}
                                scheduledEvents={scheduledEvents?.length > 0}
                                courseEvents={courseEvents?.length > 0}
                            />
                        ) : (
                            <CompetenceProfileSelector
                                setReturnValue={async (cp) => {
                                    setScheduledEvents([]); // explicit clear because we normally only populate scheduled events on an empty set and specific situations. now we want to force this
                                    setEventAllowedTrainers([]); // explicit clear here as this is done so previously and we dont want to break stuff. There are some edge-cases when making a useEffect() on competenceProfielUUID
                                    await remoteSetCompetenceProfile(course, cp.uuid);
                                    setCompetenceProfileUUID(cp.uuid);
                                    mutateRemoteEventTemplates();
                                }}
                                competenceProfileDetails={competenceProfileDetails}
                                trainer={trainer}
                                scheduledEvents={scheduledEvents?.length > 0}
                                courseEvents={courseEvents?.length > 0}
                                multipleTrainers={trainers?.length > 1}
                            />
                        )}
                    </div>

                    {competenceProfileUUID !== false && (
                        <>
                            <div className="form-section">
                                <div className="form-section__row row">
                                    <div className="col-12">
                                        <h2 className="form-section__title">Algemene cursusgegevens</h2>
                                        <div className="form-section__row row">
                                            <div className="col-6">
                                                <label className={"form-input__label"}>Cursusreferentie *</label>
                                                <input
                                                    className={cx("form-control form-control-md", {
                                                        "form-control--error": errors?.some((error) => error.field === "your_reference")
                                                    })}
                                                    type="text"
                                                    name="your_reference"
                                                    placeholder={"Vul jouw referentie in om de cursus terug te vinden"}
                                                    value={courseReference}
                                                    onChange={(e) => setCourseReference(e.target.value)}
                                                    required
                                                />
                                            </div>
                                            <div className={"col-3"}>
                                                <label className="form-input__label">Contactpersoon opleider *</label>

                                                {!trainer ? (
                                                    <input
                                                        className={"form-control form-control-md form-control--disabled"}
                                                        disabled
                                                        type={"text"}
                                                        value={"Kies eerst een opleider"}
                                                    />
                                                ) : (
                                                    <Select
                                                        name="trainerContact"
                                                        value={trainerContact}
                                                        className={cx("react-select-new", {
                                                            "form-control--error": errors?.some((error) => error.field === "trainer_contact")
                                                        })}
                                                        classNamePrefix="react-select"
                                                        onChange={setTrainerContact}
                                                        controlShouldRenderValue={true}
                                                        options={trainerContactPersons}
                                                        getOptionLabel={(trainerContact) =>
                                                            `${trainerContact.first_name}${
                                                                trainerContact.last_name_prefix ? " " + `${trainerContact.last_name_prefix}` + " " : " "
                                                            }${trainerContact.last_name}`
                                                        }
                                                        getOptionValue={(trainerContact) => trainerContact.uuid}
                                                        isClearable={true}
                                                        backspaceRemovesValue={true}
                                                        placeholder={trainers?.length > 0 ? (!trainer ? "Kies eerst een opleider" : "") : "Opleiders worden geladen"}
                                                        noOptionsMessage={() => "Geen resultaten"}
                                                        // disabled={!this.state.editable}
                                                    />
                                                )}
                                            </div>
                                            <div className="col-3">
                                                <label className={"form-input__label"}>Verwacht aantal cursisten *</label>
                                                <input
                                                    className={cx("form-control from-control-md", {
                                                        "form-control--error":
                                                            errors?.some((error) => error.field === "expected_students") ||
                                                            courseErrors?.some((error) => error.field === "expected_students")
                                                    })}
                                                    type="number"
                                                    min="1"
                                                    name="expected_students"
                                                    onChange={(e) => setExpectedStudents(e.target.value)}
                                                    value={expectedStudents}
                                                    required
                                                />
                                            </div>
                                        </div>

                                        {/*{competenceProfileDetails?.allow_employer_specific && (*/}
                                        {/*    <div className="form-section__row row">*/}
                                        {/*        <div className="col-4">*/}
                                        {/*            <label className="form-input__label">Bedrijfsspecifiek</label>*/}
                                        {/*            <div className="form-input__radio">*/}
                                        {/*                <div className="radio">*/}
                                        {/*                    <input type="radio" id="company_specific-yes" className="radio__input" name="company_specific" value="yes" checked={!!employerSpecific} onChange={ e => {*/}
                                        {/*                        setEmployerSpecific(true);*/}
                                        {/*                    }}/>*/}
                                        {/*                    <label htmlFor="company_specific-yes" className="radio__label">Ja</label>*/}
                                        {/*                </div>*/}
                                        {/*                <div className="radio">*/}
                                        {/*                    <input type="radio" id="company_specific-no" className="radio__input" name="company_specific" value="no" checked={!employerSpecific}  onChange={ e => {*/}
                                        {/*                        setEmployerSpecific(false);*/}
                                        {/*                    }}/>*/}
                                        {/*                    <label htmlFor="company_specific-no" className="radio__label">Nee</label>*/}
                                        {/*                </div>*/}
                                        {/*            </div>*/}
                                        {/*        </div>*/}
                                        {/*    </div>*/}
                                        {/*)}*/}
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    {competenceProfileUUID && (
                        <>
                            {confirmCustomEvents && (
                                <Dialog
                                    title={"Zelf toetsmomenten samenstellen"}
                                    onClose={() => setConfirmCustomEvents(false)}
                                    visible={true}
                                    animation="fade"
                                    footer={[
                                        <button
                                            type="button"
                                            className="btn btn-light"
                                            key="no"
                                            onClick={() => {
                                                setConfirmCustomEvents(false);
                                            }}
                                        >
                                            Nee, blijf standaard toetsmomenten gebruiken
                                        </button>,
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            key="yes"
                                            onClick={() => {
                                                setConfirmCustomEvents(false);
                                                setCustomEvents(true);
                                                scheduledEvents.forEach(async (sEvent) => {
                                                    let res = await remoteDeleteEvent(course, sEvent.uuid);
                                                });
                                                setScheduledEvents([]);
                                            }}
                                        >
                                            Ja, zelf toetsmomenten samenstellen
                                        </button>
                                    ]}
                                >
                                    <p className={"paragraph"}>
                                        Als je zelf toetsmomenten wilt gaan samenstellen, zullen de aanwezige (eventueel al gewijzigde) standaard toetsmomenten komen te
                                        vervallen.
                                        <br />
                                        <br />
                                        Weet je zeker dat je zelf toetsmomenten wilt samenstellen?
                                    </p>
                                </Dialog>
                            )}

                            {confirmTemplateEvents && (
                                <Dialog
                                    title={"Standaard toetsmomenten gebruiken"}
                                    onClose={() => setConfirmTemplateEvents(false)}
                                    visible={true}
                                    animation="fade"
                                    footer={[
                                        <button
                                            type="button"
                                            className="btn btn-light"
                                            key="no"
                                            onClick={() => {
                                                setConfirmTemplateEvents(false);
                                            }}
                                        >
                                            Nee, blijf eigen toetsmomenten gebruiken
                                        </button>,
                                        <button
                                            type="button"
                                            className="btn btn-primary"
                                            key="yes"
                                            onClick={() => {
                                                setConfirmTemplateEvents(false);
                                                setCustomEvents(false);
                                                setScheduledEvents(eventTemplates);
                                                setMissingEvents([]);
                                            }}
                                        >
                                            Ja, gebruik standaard toetsmomenten
                                        </button>
                                    ]}
                                >
                                    <p className={"paragraph"}>
                                        Als je de standaard toetsmomenten wilt gebruiken, zullen de aanwezige (eventueel al gewijzigde) zelf samengestelde toetsmomenten
                                        komen te vervallen.
                                        <br />
                                        <br />
                                        Weet je zeker dat je standaard toetsmomenten wilt gebruiken?
                                    </p>
                                </Dialog>
                            )}

                            <div className="form-section">
                                <div className="flex flex-justify-space-between">
                                    <h2 className="form-section__title">Toetsmomenten</h2>
                                    {/*
                                    Johan: Disabled this due to #1998 :-(
                                    <a href={"#"} className={"link"}
                                        onClick={e => {
                                            e.preventDefault();
                                            !customEvents ? setConfirmCustomEvents(true) : setConfirmTemplateEvents(true);
                                        }}
                                    >{customEvents ? <>Standaard toetsmomenten gebruiken</> : <>Zelf toetsmomenten samenstellen</>}</a>*/}
                                </div>

                                {scheduledEvents.map((eventObj, idx) => {
                                    if (eventObj.mark_delete) {
                                        if (eventObj.uuid) {
                                            return <p className="event" key={eventObj.uuid}><b>{eventObj.name}</b> is gesplitst.</p>;
                                        }
                                        return null;
                                    }
                                    let containsElearning = false;

                                    eventObj.prerequisites?.forEach((prereqUUID) => {
                                        const prereqEvent = scheduledEvents.filter((eventTemplate) => eventTemplate.template === prereqUUID)[0];
                                        if (prereqEvent.type === "elearning") {
                                            containsElearning = true;
                                        }
                                    });

                                    // Dont show e-learnings
                                    return (
                                        eventObj.type !== "elearning" && (
                                            <Event
                                                key={(eventObj.uuid || eventObj.template) + idx}
                                                competenceProfileUUID={competenceProfileUUID}
                                                event={eventObj}
                                                currentIndex={parseInt(idx)} // We need this to know what event to update (freeform events!!)
                                                // FIXME: Need to check with a CP which actually has a min_date and elearning events
                                                minDate={
                                                    containsElearning
                                                        ? moment(defaultMinDate).add(parseInt(ELEARNING_WEEKS_DEFAULT), "weeks").toDate() // append time to reserve space for its elearning(s)
                                                        : moment(defaultMinDate).toDate()
                                                }
                                                maxDate={competenceProfile?.max_date ? moment(competenceProfile.max_date).toDate() : null}
                                                locations={locations}
                                                trainer={trainer}
                                                deleteEvent={customEvents ? () => deleteEvent(parseInt(idx)) : false}
                                                splittableEvents={splittableEvents}
                                                scheduledEvents={scheduledEvents} // Using this to display information of prerequisits of the event
                                                eventErrors={eventErrors?.[idx]}
                                                updateScheduledEvent={updateScheduledEvent}
                                                setEventAllowedTrainers={setEventAllowedTrainers}
                                                isEditable={eventObj.is_editable !== false}
                                            />
                                        )
                                    );
                                })}

                                {missingEvents?.length > 0 && (
                                    <>
                                        <details className="feedback-banner feedback-banner--warning feedback-banner--warning-expandable">
                                            <summary className="feedback-banner__summary">
                                                <p className="feedback-banner__header">
                                                    Niet alle benodigde toetsmomenten zijn aanwezig. Zonder deze toetsmomenten is het niet mogelijk om deze cursus af te
                                                    ronden.
                                                </p>
                                            </summary>
                                            <ul className="feedback-banner__content default">
                                                {missingEvents
                                                    .filter((e) => e.type !== "elearning")
                                                    .map((event) => {
                                                        return <li key={event.template}>{event.name}</li>;
                                                    })}
                                            </ul>
                                        </details>
                                    </>
                                )}

                                {customEvents && (
                                    <div className="form-section__row row">
                                        <div className="form-input col-12">
                                            <label className="form-input__label">Toetsmoment toevoegen</label>
                                            <div className="flex flex-align-center flex-gap-xsmall">
                                                {/*<select className="form-control form-control-md" name="add-event-template" placeholder={""} value={addEventTemplate} onChange={e => setAddEventTemplate(e.target.value)}>*/}
                                                {/*    <option key={false} value="">Maak een keuze</option>*/}
                                                {/*    {eventTemplates.filter(e => !['elearning'].includes(e.type)).map(event => {*/}
                                                {/*        return (*/}
                                                {/*            <option key={event.template} value={event.template}>{event.template_parent ? " - ":""}{event.name}</option>*/}
                                                {/*        );*/}
                                                {/*    })}*/}
                                                {/*</select>*/}

                                                <Select
                                                    name="add-event-template"
                                                    value={extraEvent}
                                                    className="react-select-new"
                                                    classNamePrefix="react-select"
                                                    onChange={(event) => setExtraEvent(event)}
                                                    controlShouldRenderValue={true}
                                                    options={eventTemplates.filter((e) => !["elearning"].includes(e.type))}
                                                    getOptionLabel={(event) => `${event.template_parent ? " - " : ""}${event.name}`}
                                                    getOptionValue={(event) => event.template}
                                                    isClearable={true}
                                                    backspaceRemovesValue={true}
                                                    placeholder={""}
                                                    noOptionsMessage={() => "Geen resultaten"}
                                                />

                                                <button className={"btn btn-secondary"} onClick={() => addEvent(extraEvent?.template)}>
                                                    Toevoegen
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/*<div className="feedback-banner feedback-banner--neutral">*/}
                    {/*    <i className="feedback-banner__icon fa fa-info-circle" />*/}
                    {/*    <p className="feedback-banner__text">Omdat er een toetsmoment binnen 5 dagen begint worden er extra kosten in rekening gebracht.</p>*/}
                    {/*</div>*/}
                    {competenceProfileUUID && (
                        <>
                            <h2 className="form-section__title form-section__title--orange">Samenvatting van jouw cursusaanmelding</h2>
                            <CompetenceProfileCard competenceProfileUUID={competenceProfileUUID} />
                            <div className="form-section__wrapper">
                                <strong>Overzicht toetsmomenten{competenceProfileDetails?.contains_elearning && "*"}</strong> (controleer of alle ingevulde gegevens
                                kloppen)
                                <br />
                                <table className={"table-plain table-plain--full-width"}>
                                    <tbody>
                                        {scheduledEvents.map((eventObj, idx) => {
                                            const start_datetime =
                                                eventObj?.start_datetime &&
                                                new Intl.DateTimeFormat("nl-NL", formatOptions).format(
                                                    eventObj?.type === "elearning" ? dateOrNow(eventObj?.start_datetime) : new Date(eventObj?.start_datetime)
                                                );

                                            return (
                                                <tr key={eventObj.uuid + idx}>
                                                    <td>
                                                        {eventObj.mark_delete ? <del>{filterEventTypeName(eventObj?.name, eventObj.type)}</del> : filterEventTypeName(eventObj?.name, eventObj.type)}
                                                    </td>
                                                    <td>{eventTypeToNL(eventObj?.type)}</td>
                                                    <td>
                                                        {start_datetime ? (
                                                            <>
                                                                {start_datetime}
                                                                {eventObj?.type === "elearning" && <> (Activeringsdatum)</>}
                                                            </>
                                                        ) : (
                                                            <>&nbsp;</>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {eventObj?.start_datetime ? (
                                                            <>
                                                                {/*{eventObj?.type === "elearning" && (*/}
                                                                {/*    <>Activeringsdatum</>*/}

                                                                {/*)}*/}
                                                                {eventObj.type !== "elearning" && (
                                                                    <>
                                                                        {new Intl.DateTimeFormat("nl-NL", formatOptionsTime).format(new Date(eventObj.start_datetime))}
                                                                        {eventObj?.end_datetime && (
                                                                            <>
                                                                                {" "}
                                                                                -{" "}
                                                                                {new Intl.DateTimeFormat("nl-NL", formatOptionsTime).format(
                                                                                    new Date(eventObj.end_datetime)
                                                                                )}{" "}
                                                                            </>
                                                                        )}
                                                                    </>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>&nbsp;</>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {/* #2008 - Removed by request of Kevin
                                competenceProfileDetails?.contains_elearning && (
                                    <i>*E-learning accounts worden aangemaakt vanaf 4 weken voor een praktijkdag.</i>
                                )*/}
                            </div>
                        </>
                    )}

                    {errors.length > 0 && (
                        <details className="feedback-banner feedback-banner--error feedback-banner--error-expandable">
                            <summary className="feedback-banner__summary">
                                <p className="feedback-banner__header">Niet alle informatie is correct ingevuld.</p>
                            </summary>
                            {/* Frontend errors */}
                            <ul className="feedback-banner__content default">
                                {errors.map((msg) => {
                                    return <li key={msg.field}>{msg.error}</li>;
                                })}
                            </ul>
                        </details>
                    )}

                    {courseErrors?.length > 0 && (
                        <>
                            <details className="feedback-banner feedback-banner--error feedback-banner--error-expandable">
                                <summary className="feedback-banner__summary">
                                    <p className="feedback-banner__header">Niet alle informatie is correct ingevuld.</p>
                                </summary>
                                <ul className="feedback-banner__content default">
                                    {courseErrors.map((msg) => {
                                        return <li key={msg.field}>{msg.error}</li>;
                                    })}
                                </ul>
                            </details>
                        </>
                    )}
                </>
            )}
            {savingTotal > 0 && (
                <div className="my-3">
                    <ProgressBar value={saving} max={savingTotal}
                        title="Bezig met opslaan..."/>
                </div>)}

            <div className="form-footer col-12">
                {pleaseWait ? (
                    <>Een moment geduld...</>
                ) : (
                    <>
                        {!course && (
                            <Link to={"/courses/"}>
                                <span className="btn color-blue">Terug naar overzicht</span>
                            </Link>
                        )}
                        {trainer && competenceProfileUUID && (
                            <>
                                        &nbsp; &nbsp;
                                <button className="btn btn-primary no-sideflap" onClick={() => onSaveClick("/courses/")}>
                                            Opslaan en naar cursusoverzicht
                                </button>
                                        &nbsp;
                                <button className="btn btn-primary no-sideflap" onClick={() => onSaveClick("/courses/new/")}>
                                            Opslaan en nieuwe toevoegen
                                </button>
                                        &nbsp;
                                <button className="btn btn-primary" onClick={() => onSaveClick("/courses/edit/:uuid/details")}>
                                            Opslaan en naar cursusdetails
                                </button>
                            </>
                        )}
                    </>
                )}
                <Dialog
                    noClose
                    className="testmoments-date-warning-modal"
                    visible={showWarningModal}
                    animation="fade"
                    footer={[
                        <>
                            <button className="btn btn-primary no-sideflap" onClick={() => setShowWarningModal(false)}>
                                        Terug
                            </button>
                            <button className="btn btn-primary no-sideflap" onClick={() => saveCourse(showWarningModal, true)}>
                                        Ik begrijp het
                            </button>
                        </>
                    ]}
                >
                    <p className="paragraph">
                        Om in aanmerking te komen voor een compleet bhv basisdiploma of herhalingscertificaat, mag de periode tussen toetsmomenten niet langer zijn dan 93
                        dagen. Certificeren van de afzonderlijke onderdelen is uiteraard wel mogelijk, maar enkel middels deelcertificaten.
                    </p>
                </Dialog>
            </div>
        </>
    );
}
